import { Controller } from "@hotwired/stimulus"

// Contact typeahead for the Relationships add form: search-as-you-type
// against /network/relationship_candidates, picking a contact fills the
// hidden related_contact_id. Free-typed edits clear the pick so the form
// never submits a stale id.
//
// Forms that also carry a "name" target get a last row offering to add the
// typed name as a NEW contact, so you can link someone who isn't in your
// network yet (see ContactRelationshipsController#find_or_create_related).
export default class extends Controller {
    static targets = ["input", "id", "results", "submit", "name"]
    static values = { url: String, excludeId: String }

    connect() {
        this._items = []
        this._selectedIndex = -1
        this._debounceTimer = null
        this._selectedName = null
        this._onDocClick = (event) => {
            if (!this.element.contains(event.target)) this._closeResults()
        }
        document.addEventListener("click", this._onDocClick)
        this._syncSubmit()
    }

    disconnect() {
        document.removeEventListener("click", this._onDocClick)
        this._clearDebounce()
    }

    query() {
        if (this._selectedName && this.inputTarget.value.trim() !== this._selectedName) {
            this.idTarget.value = ""
            if (this.hasNameTarget) this.nameTarget.value = ""
            this._selectedName = null
            this._syncSubmit()
        }
        this._clearDebounce()
        let value = this.inputTarget.value.trim()
        // A typed name IS an answer. Save stayed disabled until you clicked the
        // "Add <name>" row, so typing a name in full and pressing Save did
        // nothing at all - no error, no hint, just a dead button. That is what
        // "I tried to add Sien as Thijs's partner but couldn't save" was.
        // Picking a real person still wins: resolve_related reads the id first,
        // and _pickExisting clears this. This only makes what you already typed
        // count for something.
        if (this.hasNameTarget && this.idTarget.value === "") this.nameTarget.value = value
        this._syncSubmit()
        if (value.length < 2) {
            this._closeResults()
            return
        }
        this._debounceTimer = window.setTimeout(() => this._fetch(value), 250)
    }

    keydown(event) {
        if (event.key === "Enter" && this.resultsTarget.hidden) {
            event.preventDefault()
            return
        }
        if (this.resultsTarget.hidden) return
        if (event.key === "ArrowDown") {
            event.preventDefault()
            this._moveSelection(1)
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            this._moveSelection(-1)
        } else if (event.key === "Enter") {
            event.preventDefault()
            if (this._selectedIndex >= 0) this._pick(this._selectedIndex)
        } else if (event.key === "Escape") {
            this._closeResults()
        }
    }

    blur() {
        window.setTimeout(() => this._closeResults(), 150)
    }

    async _fetch(value) {
        try {
            let params = new URLSearchParams({ q: value, exclude_id: this.excludeIdValue || "" })
            let response = await fetch(`${this.urlValue}?${params}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            if (!response.ok) throw new Error("candidates failed")
            let payload = await response.json()
            if (this.inputTarget.value.trim() !== value) return
            this._render(payload.contacts || [])
        } catch (_error) {
            this._closeResults()
        }
    }

    _render(contacts) {
        this._contacts = contacts
        this._items = []
        this._selectedIndex = -1
        this.resultsTarget.replaceChildren()
        let typed = this.inputTarget.value.trim()
        // Offer to create only when nothing found already goes by that name -
        // otherwise the obvious pick sits next to a near-duplicate of itself.
        let canCreate = this.hasNameTarget && typed.length >= 2 &&
            !contacts.some((contact) => (contact.name || "").trim().toLowerCase() === typed.toLowerCase())
        if (contacts.length === 0 && !canCreate) {
            this._closeResults()
            return
        }

        contacts.forEach((contact, index) => {
            let row = document.createElement("button")
            row.type = "button"
            row.className = "contact-picker-item"
            let name = document.createElement("span")
            name.className = "contact-picker-item-name"
            name.textContent = contact.name
            row.appendChild(name)
            if (contact.meta) {
                let meta = document.createElement("span")
                meta.className = "contact-picker-item-meta"
                meta.textContent = contact.meta
                row.appendChild(meta)
            }
            row.addEventListener("mousedown", (event) => event.preventDefault())
            row.addEventListener("click", () => this._pick(index))
            row.addEventListener("mouseenter", () => this._setSelection(index))
            this.resultsTarget.appendChild(row)
            this._items.push(row)
        })
        if (canCreate) this._appendCreateRow(typed)
        this.resultsTarget.hidden = false
    }

    _appendCreateRow(typed) {
        let row = document.createElement("button")
        row.type = "button"
        row.className = "contact-picker-item contact-picker-item--create"
        let name = document.createElement("span")
        name.className = "contact-picker-item-name"
        name.textContent = `Add "${typed}"`
        row.appendChild(name)
        let meta = document.createElement("span")
        meta.className = "contact-picker-item-meta"
        meta.textContent = "New contact"
        row.appendChild(meta)
        let index = this._items.length
        row.addEventListener("mousedown", (event) => event.preventDefault())
        row.addEventListener("click", () => this._pick(index))
        row.addEventListener("mouseenter", () => this._setSelection(index))
        this.resultsTarget.appendChild(row)
        this._items.push(row)
        this._createName = typed
        this._createIndex = index
    }

    _pick(index) {
        if (index === this._createIndex) return this._pickNew()

        let contact = (this._contacts || [])[index]
        if (!contact) return
        this.inputTarget.value = contact.name
        this.idTarget.value = contact.id
        if (this.hasNameTarget) this.nameTarget.value = ""
        this._selectedName = contact.name
        this._closeResults()
        this._syncSubmit()
    }

    // Defer creating the contact to the submit: the name rides along and the
    // server creates (or dedupes into) it as part of saving the link, so an
    // abandoned form leaves no stray card behind.
    _pickNew() {
        let typed = this._createName
        if (!typed) return
        this.inputTarget.value = typed
        this.idTarget.value = ""
        if (this.hasNameTarget) this.nameTarget.value = typed
        this._selectedName = typed
        this._closeResults()
        this._syncSubmit()
    }

    _syncSubmit() {
        if (!this.hasSubmitTarget) return

        let picked = this.idTarget.value !== "" || (this.hasNameTarget && this.nameTarget.value !== "")
        this.submitTarget.disabled = !picked
    }

    _moveSelection(delta) {
        if (this._items.length === 0) return
        let next = this._selectedIndex + delta
        if (next < 0) next = this._items.length - 1
        if (next >= this._items.length) next = 0
        this._setSelection(next)
    }

    _setSelection(index) {
        this._items.forEach((row, i) => row.classList.toggle("is-active", i === index))
        this._selectedIndex = index
    }

    _closeResults() {
        this.resultsTarget.hidden = true
        this.resultsTarget.replaceChildren()
        this._items = []
        this._selectedIndex = -1
        this._createIndex = -1
        this._createName = null
    }

    _clearDebounce() {
        if (this._debounceTimer) window.clearTimeout(this._debounceTimer)
        this._debounceTimer = null
    }
}
